/**
 * Pay to book — the booking page's payment choice and the admin's payment
 * labels. Pure functions only (unit-tested in tests/booking-payment-lib.spec.ts).
 *
 * The authoritative rules run in the database (migration
 * 20261102022214_pay_to_book.sql): `_booking_amount_due` mirrors
 * bookingAmountDue, `_org_booking_payment_options` mirrors the method rules,
 * and submit_customer_booking_request refuses a method the business doesn't
 * take. Keep them in step.
 */

import { computeDepositAmount, normalisePaymentPolicy, type PaymentPolicy } from './paymentPolicy';

export type BookingPayMethod = 'card' | 'paypal' | 'cash';

/** What the submit RPC expects in `_payment_method`. */
export const BOOKING_PAY_METHOD_RPC_VALUE: Record<BookingPayMethod, string> = {
  card: 'stripe',
  paypal: 'paypal',
  cash: 'cash_in_person',
};

export interface BookingAmounts {
  total: number;
  /** Charged when they book by card (deposit or the full price). */
  dueNow: number;
  /** Left to pay on the day after a card deposit. */
  dueLater: number;
  kind: 'deposit' | 'full';
}

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * Card customers pay the deposit (when the policy asks for one and it's more
 * than nothing) or the full price.
 */
export function bookingAmountDue(total: number | null | undefined, policy: Partial<PaymentPolicy> | null | undefined): BookingAmounts {
  const t = typeof total === 'number' && Number.isFinite(total) && total > 0 ? round2(total) : 0;
  const deposit = computeDepositAmount(t, normalisePaymentPolicy(policy));
  if (deposit > 0 && deposit < t) {
    return { total: t, dueNow: deposit, dueLater: round2(t - deposit), kind: 'deposit' };
  }
  return { total: t, dueNow: t, dueLater: 0, kind: 'full' };
}

export interface BookingMethodAvailability {
  /** The business's connected Stripe account can take card payments. */
  card: boolean;
  cash: boolean;
  /** PayPal is switched on with a valid PayPal.me name. */
  paypal: boolean;
}

/**
 * The ways a customer can pay for a fixed-price clean, card first. Quote-only
 * (or unpriced) services take no payment at booking, so nothing is offered.
 */
export function availableBookingPaymentMethods(
  availability: BookingMethodAvailability,
  { fixedPrice }: { fixedPrice: boolean },
): BookingPayMethod[] {
  if (!fixedPrice) return [];
  const methods: BookingPayMethod[] = [];
  if (availability.card) methods.push('card');
  if (availability.paypal) methods.push('paypal');
  if (availability.cash) methods.push('cash');
  return methods;
}

/**
 * Keep the customer's choice if it's still on offer. Otherwise card (pay to
 * book), then cash (as before card existed), then PayPal.
 */
export function pickBookingPaymentMethod(
  choice: BookingPayMethod | '' | null | undefined,
  offered: BookingPayMethod[],
): BookingPayMethod | null {
  if (choice && offered.includes(choice)) return choice;
  return (['card', 'cash', 'paypal'] as const).find((m) => offered.includes(m)) ?? null;
}

function money(amount: number, symbol: string): string {
  return `${symbol}${amount.toFixed(2)}`;
}

/** The plain line under each option: what's paid when. */
export function amountDueSummary(method: BookingPayMethod, amounts: BookingAmounts, symbol = '£'): string {
  if (amounts.total <= 0) return '';
  if (method === 'card') {
    return amounts.kind === 'deposit'
      ? `${money(amounts.dueNow, symbol)} deposit now, ${money(amounts.dueLater, symbol)} on the day`
      : `${money(amounts.total, symbol)} now`;
  }
  if (method === 'paypal') {
    return amounts.kind === 'deposit'
      ? `${money(amounts.dueNow, symbol)} deposit, ${money(amounts.dueLater, symbol)} on the day`
      : money(amounts.total, symbol);
  }
  return `${money(amounts.total, symbol)} on the day`;
}

/** Submit button label for the chosen method. */
export function bookingSubmitLabel(method: BookingPayMethod | null, amounts: BookingAmounts, symbol = '£'): string {
  if (method === 'card' && amounts.dueNow > 0) return `Pay ${money(amounts.dueNow, symbol)} and book`;
  return 'Confirm booking';
}

// ---------------------------------------------------------------------------
// Holds
// ---------------------------------------------------------------------------

/** A card hold has lapsed once its expiry time has passed (or it has none). */
export function isHoldExpired(expiresAt: string | null | undefined, now: Date = new Date()): boolean {
  if (!expiresAt) return true;
  const t = new Date(expiresAt).getTime();
  return !Number.isFinite(t) || t <= now.getTime();
}

/** "14:32" on the business's clock. */
export function formatHoldTime(expiresAt: string | null | undefined, timeZone = 'Europe/London'): string {
  if (!expiresAt) return '';
  const d = new Date(expiresAt);
  if (!Number.isFinite(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone }).format(d);
  } catch {
    return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
  }
}

// ---------------------------------------------------------------------------
// Admin labels
// ---------------------------------------------------------------------------

export interface JobPaymentFields {
  status?: string | null;
  payment_method?: string | null;
  payment_status?: string | null;
  payment_amount_paid?: number | string | null;
  payment_total?: number | string | null;
  payment_hold_expires_at?: string | null;
}

export type PaymentTone = 'green' | 'amber' | 'grey' | 'violet' | 'red';

/**
 * "Deposit paid £18", "Paid in full", "Cash on the day", "Awaiting payment
 * (expires 14:32)" … or null for jobs with no online payment.
 */
export function jobPaymentLabel(
  job: JobPaymentFields,
  { symbol = '£', timeZone = 'Europe/London', now = new Date() }: { symbol?: string; timeZone?: string; now?: Date } = {},
): { label: string; tone: PaymentTone } | null {
  const paid = Number(job.payment_amount_paid) || 0;
  switch (job.payment_status) {
    case 'awaiting_payment':
      if (job.status === 'awaiting_payment' && !isHoldExpired(job.payment_hold_expires_at, now)) {
        return { label: `Awaiting payment (expires ${formatHoldTime(job.payment_hold_expires_at, timeZone)})`, tone: 'amber' };
      }
      return { label: 'Card payment not finished', tone: 'grey' };
    case 'deposit_paid':
      return { label: `Deposit paid ${symbol}${trimMoney(paid)}`, tone: 'green' };
    case 'paid':
      return { label: 'Paid in full', tone: 'green' };
    case 'due_on_the_day':
      return { label: 'Cash on the day', tone: 'grey' };
    case 'pay_later':
      return { label: 'PayPal (check it’s arrived)', tone: 'violet' };
    case 'expired':
      return { label: 'Card payment not finished', tone: 'grey' };
    case 'refund_pending':
      return { label: `Refunding ${symbol}${trimMoney(paid)}`, tone: 'amber' };
    case 'refunded':
      return { label: `Refunded ${symbol}${trimMoney(paid)}`, tone: 'grey' };
    case 'refund_failed':
      return { label: 'Refund failed: check Stripe', tone: 'red' };
    default:
      return null;
  }
}

function trimMoney(amount: number): string {
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
}

/** Cleaners never see (or get told about) a booking that isn't paid for yet. */
export function isHeldForPayment(job: { status?: string | null }): boolean {
  return job.status === 'awaiting_payment';
}

// ---------------------------------------------------------------------------
// Payments settings: may the owner switch cash off?
// ---------------------------------------------------------------------------

/**
 * Cash can only be turned off when customers have another way to pay (card
 * that can take charges, or PayPal). Otherwise it stays on so the business can
 * still take bookings.
 */
export function resolveCashEnabled(input: { requested: boolean; cardReady: boolean; paypalReady: boolean }): boolean {
  if (!input.cardReady && !input.paypalReady) return true;
  return input.requested;
}

// ---------------------------------------------------------------------------
// The browser's note of a booking that's gone off to pay
// ---------------------------------------------------------------------------

export interface PendingBookingPayment {
  bookingId: string;
  token: string;
  email: string;
  when: string;
  isRepeat: boolean;
  /** Set once the customer access link has been sent after paying. */
  accessLinkSent?: boolean;
}

const STORAGE_PREFIX = 'tpv:booking-payment:';

export function savePendingBookingPayment(storage: Pick<Storage, 'setItem'>, pending: PendingBookingPayment): void {
  try {
    storage.setItem(`${STORAGE_PREFIX}${pending.bookingId}`, JSON.stringify(pending));
  } catch {
    // Private mode / storage full: the return page falls back to generic copy.
  }
}

export function readPendingBookingPayment(storage: Pick<Storage, 'getItem'>, bookingId: string | null | undefined): PendingBookingPayment | null {
  if (!bookingId) return null;
  try {
    const raw = storage.getItem(`${STORAGE_PREFIX}${bookingId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingBookingPayment;
    return parsed && parsed.bookingId === bookingId && /^[0-9a-f]{64}$/.test(parsed.token) ? parsed : null;
  } catch {
    return null;
  }
}

export function clearPendingBookingPayment(storage: Pick<Storage, 'removeItem'>, bookingId: string): void {
  try {
    storage.removeItem(`${STORAGE_PREFIX}${bookingId}`);
  } catch {
    // ignore
  }
}
